import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Clock, Upload, Edit, Trash2, FileText, FolderPlus, Plus, User as UserIcon } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ACTION_CONFIG = {
  import: { label: 'Importación', icon: Upload, color: 'bg-blue-100 text-blue-800' },
  import_url: { label: 'Importación URL', icon: Upload, color: 'bg-blue-100 text-blue-800' },
  create: { label: 'Creación', icon: Plus, color: 'bg-green-100 text-green-800' },
  update: { label: 'Edición', icon: Edit, color: 'bg-amber-100 text-amber-800' },
  delete: { label: 'Eliminación', icon: Trash2, color: 'bg-red-100 text-red-800' },
};

const ActivityLogPage = () => {
  const { isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isSuperAdmin) {
      navigate('/');
      return;
    }
    fetchLog();
  }, [isSuperAdmin, navigate]);

  const fetchLog = async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/activity-log?limit=200`);
      setEntries(res.data);
    } catch (error) {
      toast.error('Error al cargar log de actividad');
    } finally {
      setLoading(false);
    }
  };

  const groupByDate = (items) => {
    const groups = {};
    items.forEach(entry => {
      const date = new Date(entry.timestamp).toLocaleDateString('es-ES', {
        weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
      });
      if (!groups[date]) groups[date] = [];
      groups[date].push(entry);
    });
    return groups;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-lg text-slate-600">Cargando...</div>
      </div>
    );
  }

  const grouped = groupByDate(entries);

  return (
    <div className="p-6 md:p-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold tracking-tight mb-2" data-testid="activity-log-title">
          Log de Actividad
        </h1>
        <p className="text-sm text-slate-600">Historial de cambios de todos los usuarios y proyectos</p>
      </div>

      {entries.length === 0 ? (
        <Card className="p-8 text-center">
          <Clock className="w-12 h-12 text-slate-400 mx-auto mb-3" />
          <p className="text-slate-500">No hay actividad registrada</p>
        </Card>
      ) : (
        <div className="space-y-6">
          {Object.entries(grouped).map(([date, items]) => (
            <div key={date}>
              <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">{date}</h2>
              <div className="space-y-2">
                {items.map(entry => {
                  const config = ACTION_CONFIG[entry.action] || ACTION_CONFIG.update;
                  const Icon = config.icon;
                  return (
                    <Card
                      key={entry.id}
                      data-testid={`activity-entry-${entry.id}`}
                      className="p-4 hover:bg-slate-50 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${config.color.split(' ')[0]}`}>
                          <Icon className={`w-4 h-4 ${config.color.split(' ')[1]}`} />
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-sm">{entry.user_name || 'Usuario'}</span>
                            <Badge className={`text-[10px] ${config.color}`}>{config.label}</Badge>
                            {entry.file_type && (
                              <Badge variant="secondary" className="text-[10px]">
                                <FileText className="w-3 h-3 mr-0.5" />
                                {entry.file_type}
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-slate-500 mt-0.5">
                            Proyecto: <span className="font-medium text-slate-700">{entry.project_name}</span>
                            {entry.changes?.imported_files && (
                              <span> — Archivos: {entry.changes.imported_files.join(', ')}</span>
                            )}
                          </p>
                        </div>

                        <div className="text-xs text-slate-400 flex-shrink-0">
                          {new Date(entry.timestamp).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ActivityLogPage;
